import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Select,
  Space,
  TableProps,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import TimeDropdownIcon from "../../../assets/icons/TimeDropdownIcon";
import TopCard from "../../../components/common/TopCard";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { dbDateFormat, timeFormat } from "../../../constants/constants";
import {
  useCreateInterviewMutation,
  useGetAllJobPostQuery,
  useGetAppliedJobQuery,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { disablePastDates, formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";
import { IType } from "./InterviewList";

const backLink = "/admin/recruitment/interview-list";

type SelectedData = {
  job_application_id: number;
  interview_date?: null | Dayjs;
  interview_time?: null | Dayjs;
};

export default function InterviewCall() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isSubmit, setSubmit] = useState(false);
  const { open, handleOpen, handleClose } = useModal<any>();
  const [selectedData, setSelectedData] = useState<SelectedData[]>([]);
  const { authInfo } = useAppSelector((state) => state.auth);
  const interviewType = Form.useWatch("interview_type", form);
  const jobPostId = Form.useWatch("job_post_id", form);
  const interviewDate = Form.useWatch("interview_date", form) as Dayjs;
  const {
    isLoading: isJobLoading,
    isError: isJobError,
    error: jobError,
    data: jobData,
  } = useGetAllJobPostQuery({
    params: { skip: 0, limit: 0, business_id: authInfo?.business_id! },
  });
  const {
    isLoading: isAppliedLoading,
    isError: isAppliedError,
    error: appliedLoading,
    data: appliedData,
  } = useGetAppliedJobQuery(
    {
      params: {
        business_id: Number(authInfo?.business_id),
        job_post_id: Number(jobPostId),
        status: "short_listed",
        interview_assign: "interview_assign",
        skip: 0,
        limit: 0,
      },
    },
    {
      skip: !jobPostId,
    }
  );

  const [createInterview, { isLoading, isError, error, isSuccess }] =
    useCreateInterviewMutation();
  useError(isJobError, jobError, "Get job post data fail");
  useError(isAppliedError, appliedLoading, "Get job applied data fail");
  useError(isError, error, "Interview call submit fail");

  const onFinish = (values: any) => {
    if (!selectedData?.length) {
      toast("error", "Please select applicant");
      return;
    }
    setSubmit(true);

    const findEmpty = selectedData.find(
      (selected) =>
        selected.interview_time === undefined ||
        selected.interview_date === undefined
    );

    if (findEmpty) {
      return;
    }

    const interviews = selectedData.map((selected) => ({
      job_application_id: selected.job_application_id,
      interview_date: selected.interview_date?.format(dbDateFormat),
      interview_time: selected.interview_time?.format(timeFormat),
      interview_type: values?.interview_type,
      meeting_link: values?.meeting_link,
    }));

    const newValues = {
      job_post_id: values?.job_post_id,
      interviews,
    };

    handleOpen({
      data: newValues,
    });
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Applicant Name",
      dataIndex: "full_name",
      render: (val) => val || "n/a",
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val) => val || "n/a",
    },

    {
      title: "Phone No.",
      dataIndex: "mobile",
      render: (val) => val || "n/a",
    },
    {
      title: "Applied Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },
    {
      title: "Interview Date",
      dataIndex: "id",
      render: (val) => {
        const findData = selectedData?.find(
          (item) => item?.job_application_id === val
        );

        return (
          <div>
            <DatePicker
              format={"DD/MM/YYYY"}
              suffixIcon={<DateIcon width={16} height={18} />}
              style={{ width: "280px" }}
              size="large"
              placeholder="--/--/----"
              disabledDate={disablePastDates}
              value={findData?.interview_date}
              onChange={(date) => {
                const now = dayjs();
                const maxFutureDate = now.add(1, "year");

                if (dayjs(date).isAfter(maxFutureDate)) {
                  message.error(
                    "Interview date can't be more than one year in the future"
                  );
                  // Clear the date if invalid
                  setSelectedData((prev) =>
                    prev?.map((item) => {
                      if (item?.job_application_id === val) {
                        return {
                          ...item,
                          interview_date: undefined,
                        };
                      }
                      return item;
                    })
                  );
                  return;
                }

                setSelectedData((prev) =>
                  prev?.map((item) => {
                    if (item?.job_application_id === val) {
                      return {
                        ...item,
                        interview_date: date,
                      };
                    }
                    return item;
                  })
                );
              }}
            />

            {isSubmit && findData && !findData?.interview_date ? (
              <span className="text-sm text-error block">
                Please select interview date
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Interview Time",
      dataIndex: "id",
      render: (val) => {
        const findData = selectedData?.find(
          (item) => item?.job_application_id === val
        );

        return (
          <div>
            <DatePicker
              picker="time"
              format={timeFormat}
              suffixIcon={<TimeDropdownIcon />}
              style={{ width: "280px" }}
              size="large"
              placeholder="--:-- --"
              disabledDate={disablePastDates}
              value={findData?.interview_time}
              onChange={(date) => {
                const now = dayjs();
                const selectedTime = dayjs(date);
                const interviewDate = findData?.interview_date;

                const isToday =
                  interviewDate && dayjs(interviewDate).isSame(now, "day");

                const isInvalidTime = isToday && selectedTime.isBefore(now);

                if (isInvalidTime) {
                  message.error(
                    "For today, interview time must be in the future"
                  );

                  // Clear the input if invalid
                  setSelectedData((prev) =>
                    prev?.map((item) => {
                      if (item?.job_application_id === val) {
                        return {
                          ...item,
                          interview_time: undefined,
                        };
                      }
                      return item;
                    })
                  );
                  return;
                }

                // Set valid time
                setSelectedData((prev) =>
                  prev?.map((item) => {
                    if (item?.job_application_id === val) {
                      return {
                        ...item,
                        interview_time: date,
                      };
                    }
                    return item;
                  })
                );
              }}
            />
            {isSubmit && findData && !findData?.interview_time ? (
              <span className="text-sm text-error block">
                Please select interview time
              </span>
            ) : null}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Interview call submitted successfully");
      setSelectedData([]);
      handleClose();
      navigate(backLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, navigate]);

  return (
    <SidebarLayout title="Interview Call">
      <ConfirmModal
        open={open?.open}
        handleOk={() => createInterview(open?.data)}
        handleClose={handleClose}
        confirmTitle="Confirm"
        questionText="Are you sure you want to schedule an interview call?"
        okText="Confirm"
        okLoading={isLoading}
      />

      <TopCard
        size={16}
        align="center"
        leftChildren={
          <Link to={backLink}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        <Button icon={<SettingIcon />} type="primary" className="sm-button" />
      </TopCard>

      <div className="bg-white rounded-md border border-extra-light p-4">
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-4">
            <Form.Item
              name="job_post_id"
              label="Job Post"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please select job post",
                },
              ]}
            >
              <Select
                placeholder="Select job post"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={jobData?.data?.jobPosts?.map((job) => ({
                  label: job?.job_title,
                  value: job?.id,
                }))}
                suffixIcon={<SelectDropdownLgIcon />}
                loading={isJobLoading}
              />
            </Form.Item>
            <Form.Item
              name="interview_type"
              label="Interview Type"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter interview type",
                },
              ]}
            >
              <Select
                placeholder="Select..."
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={Object.entries(IType).map(([value, label]) => ({
                  label,
                  value,
                }))}
                suffixIcon={<SelectDropdownLgIcon />}
              />
            </Form.Item>
            {/* <Form.Item
              name="interview_date"
              label="Interview Date"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please select interview date",
                },
              ]}
            >
              <DatePicker
                format={"DD/MM/YYYY"}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
                placeholder="dd/mm/yyyy"
                disabledDate={disablePastDates}
              />
            </Form.Item> */}

            {/* <Form.Item
              name="interview_method"
              label="Interview Method"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter interview method",
                },
              ]}
            >
              <Select
                placeholder="Select..."
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={Object.entries(IType).map(([value, label]) => ({
                  label,
                  value,
                }))}
                suffixIcon={<SelectDropdownLgIcon />}
              />
            </Form.Item> */}

            {interviewType === "Google Meet" ? (
              <Form.Item
                name="meeting_link"
                label="Google Meet Link"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter google meet link",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const googleMeetRegex =
                        /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

                      if (!googleMeetRegex.test(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid Google Meet link")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input size="large" placeholder="Type google meet" />
              </Form.Item>
            ) : null}
          </div>

          <CustomTable
            loading={isAppliedLoading}
            columns={columns}
            dataSource={addKeyInArray(appliedData?.data?.applications)}
            className="header-table-color"
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selectedData.map(
                (item) => item?.job_application_id
              ),
              onChange: (keys) => {
                const selected = keys.map((key) => {
                  const findData = selectedData?.find(
                    (item) => item?.job_application_id === Number(key)
                  );

                  return {
                    job_application_id: Number(key),
                    interview_date:
                      findData?.interview_date ||
                      (interviewDate ? interviewDate : undefined),
                    interview_time: findData?.interview_time || undefined,
                  };
                });

                setSelectedData(selected);
              },
              columnWidth: 40,
            }}
          />

          <Space className="w-full mt-6" size={12}>
            {/* <Link to={backLink}>
              <Button className="normal-dark-button modal-button" size="middle">
                Cancel
              </Button>
            </Link> */}
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              disabled={!selectedData?.length || isLoading}
            >
              Submit
            </Button>
          </Space>
        </Form>
      </div>
    </SidebarLayout>
  );
}
